import { useState, useEffect } from 'react';
import { supabase, Database } from '../lib/supabase';
import { validateUUID, validateSubject, validateLearningObjectives, validateCustomPrompt, checkRateLimit } from '../lib/validation';
import { validateChildOwnership } from '../lib/security';
import { useAuth } from './useAuth';

type AIActivity = Database['public']['Tables']['ai_generated_activities']['Row'];

export function useAIActivities(childId: string | null) {
  const [aiActivities, setAIActivities] = useState<AIActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchAIActivities = async () => {
    if (!childId) {
      setAIActivities([]);
      return;
    }

    setLoading(true);
    try {
      console.log('Fetching AI activities for child:', childId);
      
      // First, get all activities without filters to debug
      const { data: allData, error: allError } = await supabase
        .from('ai_generated_activities')
        .select('id, title, status, expires_at, created_at, child_id')
        .eq('child_id', childId)
        .order('created_at', { ascending: false });
        
      if (allError) {
        console.error('Error fetching all activities:', allError);
      } else {
        console.log('All AI activities for child:', allData);
        console.log('Current time:', new Date().toISOString());
      }
      
      // Now get filtered activities
      const { data, error } = await supabase
        .from('ai_generated_activities')
        .select('*')
        .eq('child_id', childId)
        .in('status', ['active', 'completed'])
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log('Filtered AI activities:', data);
      console.log('Number of activities after filtering:', data?.length || 0);
      
      setAIActivities(data || []);
    } catch (error) {
      console.error('Error fetching AI activities:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAIActivities();
  }, [childId]);

  const generateActivity = async (
    subject: 'math' | 'reading' | 'science',
    curriculumTargetId?: string,
    customPrompt?: string,
    learningObjectives?: string[]
  ) => {
    // Validation checks
    if (!childId) return { error: new Error('No child selected') };
    if (!user) return { error: new Error('User not authenticated') };

    // Validate inputs
    const childIdValidation = validateUUID(childId);
    if (!childIdValidation.isValid) {
      return { error: new Error('Invalid child ID format') };
    }

    const subjectValidation = validateSubject(subject);
    if (!subjectValidation.isValid) {
      return { error: new Error('Invalid subject selection') };
    }

    if (curriculumTargetId) {
      const targetIdValidation = validateUUID(curriculumTargetId);
      if (!targetIdValidation.isValid) {
        return { error: new Error('Invalid curriculum target ID format') };
      }
    }

    if (customPrompt) {
      const promptValidation = validateCustomPrompt(customPrompt);
      if (!promptValidation.isValid) {
        return { error: new Error(promptValidation.errors[0]?.message || 'Invalid custom prompt') };
      }
    }

    if (learningObjectives && learningObjectives.length > 0) {
      const objectivesValidation = validateLearningObjectives(learningObjectives);
      if (!objectivesValidation.isValid) {
        return { error: new Error(objectivesValidation.errors[0]?.message || 'Invalid learning objectives') };
      }
    }

    // Rate limiting - max 5 requests per minute per child
    const rateLimitKey = `ai-activity-${childId}`;
    if (!checkRateLimit(rateLimitKey, 5, 60000)) {
      return { error: new Error('Too many activity generation requests. Please wait before trying again.') };
    }

    // Validate child ownership
    const ownershipValidation = await validateChildOwnership(childId, user.id, supabase);
    if (!ownershipValidation.isValid) {
      return { error: new Error('Unauthorized: You do not have permission to generate activities for this child') };
    }

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-activity`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'X-Client-Info': 'neurocademy-web',
        },
        body: JSON.stringify({
          childId,
          subject: subjectValidation.data,
          curriculumTargetId,
          customPrompt: customPrompt ? validateCustomPrompt(customPrompt).data : undefined,
          learningObjectives: learningObjectives ? validateLearningObjectives(learningObjectives).data : undefined
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed with status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.activity) {
        // Add the new activity to the beginning of the list
        setAIActivities(prev => [result.activity, ...prev]);
        return { data: result.activity, error: null };
      } else {
        throw new Error(result.error || 'Failed to generate activity');
      }
    } catch (error) {
      console.error('Error generating AI activity:', error);
      return { 
        data: null, 
        error: error instanceof Error ? error : new Error('Unknown error occurred')
      };
    }
  };

  const markAsCompleted = async (activityId: string) => {
    try {
      const { error } = await supabase
        .from('ai_generated_activities')
        .update({ status: 'completed' })
        .eq('id', activityId);

      if (error) throw error;

      setAIActivities(prev => 
        prev.map(activity => 
          activity.id === activityId 
            ? { ...activity, status: 'completed' }
            : activity
        )
      );
    } catch (error) {
      console.error('Error marking AI activity as completed:', error);
    }
  };

  // Add a method to manually add an activity to the list
  const addActivity = (activity: AIActivity) => {
    setAIActivities(prev => [activity, ...prev]);
  };

  return {
    aiActivities,
    loading,
    generateActivity,
    markAsCompleted,
    addActivity,
    refetch: fetchAIActivities,
  };
}